import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { lazy, Suspense, useEffect } from 'react'
import App from './App'
import ScanCapturePage from '@components/ScanCapture/ScanCapturePage'
import LoginPage from '@components/Auth/LoginPage'
import ShopsPage from '@components/Shops/ShopsPage'
import ShopDetailPage from '@components/Shops/ShopDetailPage'
import ClaimPage from '@components/Shops/ClaimPage'
import ShopsMapPage from '@components/Shops/ShopsMapPage'
import LocationPickerPage from '@components/Shops/LocationPickerPage'
import PublicShopPage from '@components/Shops/PublicShopPage'
import EntrancePage from '@components/Entrance/EntrancePage'
import PublicShopBrowser from '@components/Shops/PublicShopBrowser'
import PublicShopMap from '@components/Shops/PublicShopMap'
import AuthGate from '@components/Auth/AuthGate'
import { BackHomeButton } from '@components/Navigation/BackHomeButton'
import { useAuthStore } from '@/stores/authStore'
import { apiUrl } from '@/config'
import './index.css'

// i18n + Arabic font
import './i18n'
import '@fontsource/cairo/400.css'
import '@fontsource/cairo/700.css'
import { I18nRoot } from './I18nRoot'

// Lazy-loaded delivery pages
const CheckoutPage = lazy(() => import('@/features/delivery/pages/CheckoutPage'))
const OrdersPage = lazy(() => import('@/features/delivery/pages/OrdersPage'))
const ShopDashboardPage = lazy(() => import('@/features/delivery/pages/ShopDashboardPage'))
const ChatPage = lazy(() => import('@/features/delivery/pages/ChatPage'))
const LiveTrackingPage = lazy(() => import('@/features/delivery/pages/LiveTrackingPage'))
const SharedOrderPage = lazy(() => import('@/features/delivery/pages/SharedOrderPage'))
const TripPage = lazy(() => import('@/features/delivery/pages/TripPage'))
const TripsListPage = lazy(() => import('@/features/delivery/pages/TripsListPage'))
const SharedTripPage = lazy(() => import('@/features/delivery/pages/SharedTripPage'))
const WishListPage = lazy(() => import('@/features/delivery/pages/WishListPage'))
const ProfilePage = lazy(() => import('@/components/Profile/ProfilePage'))

// Load debug utilities for console testing
import '@utils/debugUtils'
import { setupDebugConsole } from '@utils/debugConsole'

// Expose THREE.js to window for standalone 3D viewer
// This ensures the viewer in public/3d-viewer/ uses the same THREE instance
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

window.THREE = THREE
window.OrbitControls = OrbitControls

// Parse URL parameters for direct shop access
const urlParams = new URLSearchParams(window.location.search)
const shopIdFromUrl = urlParams.get('shopid')

console.log('🔗 [main.tsx] URL parameters:', { shopid: shopIdFromUrl, fullUrl: window.location.href })

// Setup debug console
setupDebugConsole()

function ThemeColorSync() {
  const { pathname } = useLocation()
  useEffect(() => {
    const color = pathname === '/' ? '#000000' : '#0f172a'
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    if (meta) meta.content = color
    document.documentElement.style.backgroundColor = color
  }, [pathname])
  return null
}

// Hydrate auth store globally: if a session token exists in localStorage,
// restore the user profile so all pages (including public shop viewer) know the user.
;(async () => {
  const stored = localStorage.getItem('dukkan_token')
  if (stored && !useAuthStore.getState().user) {
    try {
      const res = await fetch(apiUrl('/api/auth/me'), {
        headers: { Authorization: `Bearer ${stored}` },
      })
      if (res.ok) {
        const user = await res.json()
        useAuthStore.getState().setAuth(user, stored)
        console.log('🔑 [main] Auth restored:', user.name)
      } else {
        localStorage.removeItem('dukkan_token')
      }
    } catch {
      // Network error — leave unauthed, don't remove token
    }
  }
})()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <Suspense fallback={<div style={{background:'#0f172a',color:'#f8fafc',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}>
  <I18nRoot>
  <BrowserRouter>
    <ThemeColorSync />
    <BackHomeButton />
    <Routes>
      {/* Entrance — "/" shows hub page UNLESS ?shopid= is present (backward compat) */}
      <Route path="/" element={shopIdFromUrl ? <App initialShopId={shopIdFromUrl} /> : <EntrancePage />} />
      {/* Public discovery (no auth) */}
      <Route path="/browse" element={<PublicShopBrowser />} />
      <Route path="/map" element={<PublicShopMap />} />
      <Route path="/shop/:shopId" element={<PublicShopPage />} />
      <Route path="/claim/:token" element={<ClaimPage />} />
      {/* Auth */}
      <Route path="/login" element={<LoginPage />} />
      {/* Owner pages (auth-gated) */}
      <Route path="/shops" element={<AuthGate><ShopsPage /></AuthGate>} />
      <Route path="/shops/map" element={<AuthGate><ShopsMapPage /></AuthGate>} />
      <Route path="/shops/:id" element={<AuthGate><ShopDetailPage /></AuthGate>} />
      <Route path="/location-picker" element={<AuthGate><LocationPickerPage /></AuthGate>} />
      <Route path="/scan" element={<AuthGate><ScanCapturePage /></AuthGate>} />
      {/* Delivery / orders (lazy) */}
      <Route path="/checkout/:shopId" element={<Suspense fallback={<div style={{background:'#0f172a',color:'#f8fafc',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><CheckoutPage /></Suspense>} />
      <Route path="/orders" element={<AuthGate><Suspense fallback={<div style={{background:'#0f172a',color:'#f8fafc',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><OrdersPage /></Suspense></AuthGate>} />
      <Route path="/orders/dashboard" element={<AuthGate><Suspense fallback={<div style={{background:'#0f172a',color:'#f8fafc',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><OrdersPage /></Suspense></AuthGate>} />
      <Route path="/dashboard/:shopId" element={<AuthGate><Suspense fallback={<div style={{background:'#0f172a',color:'#f8fafc',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><ShopDashboardPage /></Suspense></AuthGate>} />
      <Route path="/chat/:orderId" element={<AuthGate><Suspense fallback={<div style={{background:'#0f172a',color:'#f8fafc',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><ChatPage /></Suspense></AuthGate>} />
      <Route path="/tracking/:orderId" element={<AuthGate><Suspense fallback={<div style={{background:'#0f172a',color:'#f8fafc',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><LiveTrackingPage /></Suspense></AuthGate>} />
      {/* Public delivery */}
      <Route path="/d/:token" element={<Suspense fallback={<div style={{background:'#0f172a',color:'#f8fafc',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><SharedOrderPage /></Suspense>} />
      <Route path="/dt/:token" element={<Suspense fallback={<div style={{background:'#0f172a',color:'#f8fafc',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><SharedTripPage /></Suspense>} />
      <Route path="/w/:token" element={<Suspense fallback={<div style={{background:'#0f172a',color:'#f8fafc',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><WishListPage /></Suspense>} />
      {/* Profile */}
      <Route path="/profile" element={<AuthGate><Suspense fallback={<div style={{background:'#0f172a',color:'#f8fafc',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><ProfilePage /></Suspense></AuthGate>} />
      {/* Trips (auth-gated) */}
      <Route path="/trip/:tripId" element={<AuthGate><Suspense fallback={<div style={{background:'#0f172a',color:'#f8fafc',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><TripPage /></Suspense></AuthGate>} />
      <Route path="/trips" element={<AuthGate><Suspense fallback={<div style={{background:'#0f172a',color:'#f8fafc',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><TripsListPage /></Suspense></AuthGate>} />
      {/* Catch-all → 3D viewer */}
      <Route path="*" element={<App initialShopId={shopIdFromUrl} />} />
    </Routes>
  </BrowserRouter>
  </I18nRoot>
  </Suspense>
)
